import { queryKeys } from "./query-keys.js";
/**
 * Centralised invalidation helpers for the surfaces HealthOS renders.
 *
 * Each helper invalidates the set of queries a given mutation touches, so a
 * an answered moment or a manual log does not have to
 * re-list its dependents at every call site. Both web and mobile call these.
 */
/** Today surface: brief, signal timeline (any window), connections, steps today. */
export function invalidateTodaySurfaces(queryClient) {
    queryClient.invalidateQueries({ queryKey: queryKeys.brief });
    queryClient.invalidateQueries({ queryKey: queryKeys.signalTimelinePrefix });
    queryClient.invalidateQueries({ queryKey: queryKeys.connections });
}
/** "Right now" surface: the ambient card and what it reads. */
export function invalidateNowSurfaces(queryClient) {
    queryClient.invalidateQueries({ queryKey: queryKeys.ambient });
}
/** Challenge workspace: routes, current enrollment, company progress, and standings. */
export function invalidateChallengeWorkspace(queryClient) {
    queryClient.invalidateQueries({ queryKey: queryKeys.challengeWorkspace });
    queryClient.invalidateQueries({ queryKey: queryKeys.companyAwardStats });
    queryClient.invalidateQueries({ queryKey: queryKeys.routeAwardCollection });
    queryClient.invalidateQueries({ queryKey: queryKeys.challengeGroupProgressPrefix });
    queryClient.invalidateQueries({ queryKey: queryKeys.companyWalkingChallenge });
    queryClient.invalidateQueries({ queryKey: queryKeys.challengeLeaderboards });
}
/**
 * Walking groups and every surface that renders a group: the group list, the
 * per-group roster, and the route maps whose group markers come from the same
 * memberships.
 */
export function invalidateWalkingGroups(queryClient) {
    queryClient.invalidateQueries({ queryKey: queryKeys.walkingGroups });
    queryClient.invalidateQueries({ queryKey: queryKeys.challengeGroupProgressPrefix });
}
/** Library surface: collection items and brief history. */
export function invalidateLibrarySurfaces(queryClient) {
    queryClient.invalidateQueries({ queryKey: queryKeys.library });
    queryClient.invalidateQueries({ queryKey: queryKeys.libraryRecommendations });
    queryClient.invalidateQueries({ queryKey: queryKeys.briefHistory });
}
/** Everything the Snapshot reads: the scaffold, its narrative, the drill-ins and the score. */
export function invalidateSnapshotSurfaces(queryClient) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.snapshot });
    void queryClient.invalidateQueries({ queryKey: queryKeys.snapshotNarrative });
    void queryClient.invalidateQueries({ queryKey: queryKeys.snapshotDetail });
    void queryClient.invalidateQueries({ queryKey: queryKeys.score });
}
/**
 * Every Logbook read: both the per-pillar trend chart and the paginated
 * history list, across every day-range/filter combination (the prefix
 * forms). A manual edit, delete, or device-entry flag can change any tab's
 * chart and list at once (an edited `occurredAt` can move an entry between
 * day buckets, or even pillars for a confounder/free-text re-type), so this
 * invalidates broadly rather than trying to target just the active tab.
 */
export function invalidateLogbookSurfaces(queryClient) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.logbookTrendPrefix });
    void queryClient.invalidateQueries({ queryKey: queryKeys.logbookHistoryPrefix });
    // Snapshot's day cards and calendar dots are built from the same Logbook reads.
    void queryClient.invalidateQueries({ queryKey: queryKeys.snapshotDayPrefix });
    void queryClient.invalidateQueries({ queryKey: queryKeys.snapshotCalendarPrefix });
}
/** Every surface built from device readings, without the model-written narrative. */
export function invalidateDeviceDataSurfaces(queryClient) {
    void queryClient.invalidateQueries({ queryKey: queryKeys.signalTimelinePrefix });
    // Every snapshot key shares the scaffold's prefix, the narrative included, so match by predicate.
    void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKeys.snapshot.every((part, i) => queryKey[i] === part) &&
            !queryKeys.snapshotNarrative.every((part, i) => queryKey[i] === part),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.score });
    invalidateLogbookSurfaces(queryClient);
    invalidateChallengeWorkspace(queryClient);
}
/** Coaching, both sides: programme, sessions, slots, threads, questionnaires and coach lists share one prefix. */
export function invalidateCoachingSurfaces(queryClient) {
    return queryClient.invalidateQueries({ queryKey: queryKeys.coaching });
}
//# sourceMappingURL=query-invalidation.js.map